import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { BarcodeScanner } from "@/components/wawi/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScanLine, Minus, Plus, Trash2, PackageCheck, Smartphone, AlertCircle } from "lucide-react";

// Variant scan at goods receipt: scan a variant EAN → resolve article+variant →
// accessories increment a quantity line (+1 per scan), serialized articles open device capture.
export default function WawiReceiptScan() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "wawi:write");
  const [wh, setWh] = useState("");
  const [lines, setLines] = useState([]);           // [{article_id, variant_id, key, article_name, variant_name, quantity, sale_price}]
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unknown, setUnknown] = useState(null);     // { code }
  const [device, setDevice] = useState(null);       // { code, article_id, variant_id, names... , imei_required, serial_required }
  const manualRef = useRef(null);

  const { data: whs } = useQuery({ queryKey: ["wawi-warehouses"], queryFn: async () => (await api.get("/wawi/warehouses")).data });
  const warehouses = whs?.items || [];

  const bump = (hit, by = 1) => {
    const key = `${hit.article_id}:${hit.variant_id || ""}`;
    setLines((prev) => {
      const i = prev.findIndex((l) => l.key === key);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], quantity: Math.max(0, next[i].quantity + by) };
        return next;
      }
      if (by <= 0) return prev;
      return [...prev, {
        key, article_id: hit.article_id, variant_id: hit.variant_id || "",
        article_name: hit.article_name, variant_name: hit.variant_name,
        quantity: by, sale_price: hit.sale_price || 0,
      }];
    });
  };

  const resolve = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    try {
      const { data: hit } = await api.get(`/wawi/lookup/ean?code=${encodeURIComponent(c)}`);
      if (hit.serialized) {
        setDevice({ ...hit, imei1: "", imei2: "", serial: "" });
        toast.info(`Serialisiert: ${hit.article_name}${hit.variant_name ? ` · ${hit.variant_name}` : ""} – IMEI/SN erfassen`);
      } else {
        bump(hit, 1);
        toast.success(`${hit.article_name}${hit.variant_name ? ` · ${hit.variant_name}` : ""} +1`);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setUnknown({ code: c });
      else toast.error(apiError(err));
    }
  };

  const onScan = (v) => { setScan(false); resolve(v); };
  const onManual = (e) => {
    e.preventDefault();
    const v = manualRef.current?.value || "";
    if (v.trim()) { resolve(v); manualRef.current.value = ""; manualRef.current.focus(); }
  };

  const setQty = (key, q) => setLines((prev) => prev.map((l) => l.key === key ? { ...l, quantity: Math.max(0, q) } : l));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const totalUnits = lines.reduce((s, l) => s + l.quantity, 0);

  const book = async () => {
    if (!wh) return toast.error("Bitte Ziellager wählen");
    const payload = {
      warehouse_id: wh,
      lines: lines.filter((l) => l.quantity > 0).map((l) => ({ article_id: l.article_id, variant_id: l.variant_id, quantity: l.quantity })),
      note: "Varianten-Scan Wareneingang",
    };
    if (payload.lines.length === 0) return toast.error("Keine Positionen zum Buchen");
    setBusy(true);
    try {
      const { data } = await api.post("/wawi/goods-receipt", payload);
      toast.success(`Wareneingang gebucht · ${data.units_booked} Einheiten`);
      setLines([]);
      qc.invalidateQueries({ queryKey: ["wawi-stock"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Wareneingang (Scan)" description="EAN einer Variante scannen – die Menge wird direkt auf die richtige Variante gebucht." />

      <SectionCard className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5 sm:w-64">
            <Label>Ziellager</Label>
            <Select value={wh} onValueChange={setWh}>
              <SelectTrigger data-testid="scan-warehouse-select"><SelectValue placeholder="Lager wählen" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <form onSubmit={onManual} className="flex flex-1 items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label>EAN scannen / eingeben</Label>
              <Input ref={manualRef} placeholder="EAN der Variante…" inputMode="numeric" autoFocus data-testid="scan-manual-input" disabled={!write} />
            </div>
            <Button type="submit" variant="outline" disabled={!write} data-testid="scan-manual-submit">Erfassen</Button>
            <Button type="button" className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setScan(true)} disabled={!write} data-testid="scan-open-camera">
              <ScanLine className="h-4 w-4" /><span className="hidden sm:inline">Kamera</span>
            </Button>
          </form>
        </div>
      </SectionCard>

      <SectionCard
        title="Erfasste Positionen"
        action={lines.length > 0 && <Badge variant="secondary" data-testid="scan-total-units">{totalUnits} Einheiten</Badge>}
      >
        {lines.length === 0 ? (
          <EmptyState icon={ScanLine} title="Noch nichts gescannt" description="Scannen Sie eine Varianten-EAN, um Positionen hinzuzufügen." />
        ) : (
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid="scan-line">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{l.article_name}{l.variant_name && <span className="text-muted-foreground"> · {l.variant_name}</span>}</div>
                  <div className="text-xs text-muted-foreground">{l.variant_name ? "Variante" : "Standard"}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(l.key, l.quantity - 1)} data-testid="scan-line-minus"><Minus className="h-3.5 w-3.5" /></Button>
                  <Input type="number" value={l.quantity} onChange={(e) => setQty(l.key, Number(e.target.value) || 0)} className="h-8 w-16 text-center" data-testid="scan-line-qty" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(l.key, l.quantity + 1)} data-testid="scan-line-plus"><Plus className="h-3.5 w-3.5" /></Button>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeLine(l.key)} data-testid="scan-line-remove"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <Button onClick={book} disabled={busy || !write} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="scan-book-button">
                <PackageCheck className="h-4 w-4" />Wareneingang buchen
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <BarcodeScanner open={scan} onOpenChange={setScan} title="Varianten-EAN scannen" onResult={onScan} />
      <UnknownEanDialog unknown={unknown} onClose={() => setUnknown(null)} onAssigned={(hit) => { setUnknown(null); resolve(hit.ean); }} />
      <DeviceCaptureDialog device={device} warehouseId={wh} onClose={() => setDevice(null)} onDone={() => { setDevice(null); qc.invalidateQueries({ queryKey: ["wawi-devices"] }); }} />
    </div>
  );
}

// Assign a scanned, unknown EAN to an existing article/variant inline, then re-resolve.
function UnknownEanDialog({ unknown, onClose, onAssigned }) {
  const [articleId, setArticleId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: arts } = useQuery({ queryKey: ["wawi-art-assign"], queryFn: async () => (await api.get("/wawi/articles?limit=200")).data, enabled: !!unknown });
  const articles = arts?.items || [];
  const article = articles.find((a) => a.id === articleId);
  const variants = article?.variants || [];

  const assign = async () => {
    if (!articleId) return toast.error("Bitte Artikel wählen");
    setBusy(true);
    try {
      await api.post(`/wawi/articles/${articleId}/assign-ean`, { code: unknown.code, variant_id: variantId });
      toast.success("EAN zugeordnet");
      setArticleId(""); setVariantId("");
      onAssigned({ ean: unknown.code });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!unknown} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="unknown-ean-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-amber-500" />EAN unbekannt</DialogTitle>
          <DialogDescription>Die EAN <span className="font-mono font-medium">{unknown?.code}</span> ist keiner Variante zugeordnet. Jetzt einem Artikel/einer Variante zuweisen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Artikel</Label>
            <Select value={articleId} onValueChange={(v) => { setArticleId(v); setVariantId(""); }}>
              <SelectTrigger data-testid="assign-article-select"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
              <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {variants.length > 0 && (
            <div className="space-y-1.5"><Label>Variante</Label>
              <Select value={variantId || "none"} onValueChange={(v) => setVariantId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="assign-variant-select"><SelectValue placeholder="Variante wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Artikel-EAN (keine Variante)</SelectItem>
                  {variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={assign} disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="assign-ean-submit">Zuordnen & erfassen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Capture a serialized device (IMEI/SN) prefilled from the scanned variant.
function DeviceCaptureDialog({ device, warehouseId, onClose, onDone }) {
  const [f, setF] = useState({ imei1: "", imei2: "", serial: "" });
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!warehouseId) return toast.error("Bitte zuerst oben ein Ziellager wählen");
    setBusy(true);
    try {
      await api.post("/wawi/devices", {
        article_id: device.article_id, variant_id: device.variant_id || "",
        imei1: f.imei1, imei2: f.imei2, serial: f.serial, warehouse_id: warehouseId,
      });
      toast.success("Gerät erfasst");
      setF({ imei1: "", imei2: "", serial: "" });
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!device} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="device-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="h-4 w-4" />Gerät erfassen</DialogTitle>
          <DialogDescription>{device?.article_name}{device?.variant_name ? ` · ${device.variant_name}` : ""} – serialisiert. IMEI/Seriennummer scannen oder eingeben.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>IMEI 1{device?.imei_required ? " *" : ""}</Label>
            <div className="flex gap-2">
              <Input value={f.imei1} onChange={(e) => set("imei1", e.target.value)} inputMode="numeric" data-testid="capture-imei1" />
              <Button type="button" variant="outline" size="icon" onClick={() => setScan(true)} data-testid="capture-imei-scan"><ScanLine className="h-4 w-4" /></Button>
            </div></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>IMEI 2</Label><Input value={f.imei2} onChange={(e) => set("imei2", e.target.value)} inputMode="numeric" /></div>
            <div className="space-y-1.5"><Label>Seriennummer{device?.serial_required ? " *" : ""}</Label><Input value={f.serial} onChange={(e) => set("serial", e.target.value)} data-testid="capture-serial" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="capture-save">Erfassen</Button>
          </DialogFooter>
        </form>
        <BarcodeScanner open={scan} onOpenChange={setScan} title="IMEI scannen" onResult={(v) => { set("imei1", v); setScan(false); }} />
      </DialogContent>
    </Dialog>
  );
}
